// 手机端展开长信息并让表单/操作换行，避免联系人、通知内容和相机错误提示超出屏幕；桌面保留原排布。
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { payStageByKey } from "../data/constants";
import { fmtMoney } from "../lib/appUtils";
import api from "../lib/api";
import ReminderPrefs from "../components/ReminderPrefs";
import TeamReminderOverview from "../components/TeamReminderOverview";
import { OverrideApproveCard, UnfinishedDigestPanel, PhotoReminderPanel, OverrideReviewPanel, EscalationResolvedPanel, PmRankingPanel } from "../components/ReminderFinanceCards";
import { toast } from "../components/ui/sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Bell, BellRing, Send, CheckCircle2, FileText, BellOff, AtSign, AlertTriangle, ArrowRight, MessageSquarePlus, Copy, ExternalLink, Clock, ChevronDown, ChevronUp, TrendingUp, TrendingDown, Minus, PhoneIncoming, Voicemail, PhoneCall, CheckCheck, Layers, Building2, CalendarCheck, Banknote } from "lucide-react";
import { fmtD } from "../lib/date";
import { T } from "../lib/i18n";
import { HandoverInline } from "../components/HandoverInline";
import { PersonnelBusinessInbox } from "../components/hrms/PersonnelBusinessInbox";
import { NudgeReplyInline } from "../components/NudgeReplyInline";
import { PoolAssignedInline } from "../components/PoolAssignedInline";
import { DutyInline } from "../components/DutyInline";
import { RecycleRestoreInline } from "../components/RecycleRestoreInline";
import { StaleContactInline } from "../components/StaleContactInline";
import { CalibDecideInline } from "../components/CalibDecideInline";
import { reminderLink } from "../components/SubcontractOps";
import { CoInvoiceOneClick } from "../components/co/CoInvoiceOneClick";
import { HandoverReassignInline } from "../components/engine/HandoverReassignInline";

// 分类筛选条的选中配色（反馈 FB973a773a3a）：评论=天蓝、催办=琥珀、系统=深灰，与铃铛下拉保持一致。
const REM_CAT_ACTIVE = { comment: "bg-sky-600 text-white border-sky-600", nudge: "bg-amber-500 text-white border-amber-500", system: "bg-slate-900 text-white border-slate-900" };

const META = {
  invoice: { icon: Send, tint: "#d97706", key: "reminderMsg" },
  // 概念方案催办（反馈 FB973a773a3a 补充留言）：一次几十条，统一用琥珀色铃铛图标，便于一眼认出是催办档
  concept_nudge: { icon: BellRing, tint: "#d97706" },
  concept_escalation: { icon: BellRing, tint: "#e11b22" },
  concept_client_nudge: { icon: BellRing, tint: "#d97706" },
  invoiced: { icon: FileText, tint: "#2563eb", key: "invoiceSent" },
  paid: { icon: CheckCircle2, tint: "#16a34a", key: "paymentReceived" },
  mention: { icon: AtSign, tint: "#4F6BFF" },
  change_order: { icon: MessageSquarePlus, tint: "#6366f1" },
  payment_request_step: { icon: Banknote, tint: "#7c3aed" },
  payment_check_pending: { icon: Banknote, tint: "#7c3aed" },
  sub_co_owner_pays: { icon: Banknote, tint: "#d97706" },
  todo_overdue_mgr: { icon: AlertTriangle, tint: "#dc2626" },
  phase_overdue: { icon: AlertTriangle, tint: "#e11b22" },
  collection_overdue: { icon: AlertTriangle, tint: "#d97706" },
  sales_target: { icon: AlertTriangle, tint: "#d97706" },
  quote_stalled: { icon: Clock, tint: "#d97706" },
  quote_stalled_escalated: { icon: AlertTriangle, tint: "#e11b22" },
  quote_accepted: { icon: CheckCircle2, tint: "#16a34a" },
  proposal_viewed: { icon: Bell, tint: "#6366f1" },
  estimate_expiry: { icon: Clock, tint: "#d97706" },
  settlement_locked: { icon: CheckCircle2, tint: "#16a34a" },
  settlement_pending: { icon: Bell, tint: "#0d9488" },
  chat_post_failed: { icon: AlertTriangle, tint: "#dc2626" },
  manager_nudge: { icon: Bell, tint: "#d97706" },
  perf_month_report: { icon: Bell, tint: "#6366f1" },
  signing_nudge: { icon: Clock, tint: "#d97706" },
  price_approval: { icon: AlertTriangle, tint: "#e11b22" },
  price_approved: { icon: CheckCircle2, tint: "#16a34a" },
  price_pending: { icon: Clock, tint: "#d97706" },
  site_visit_request: { icon: Bell, tint: "#4F6BFF" },
  new_lead: { icon: Bell, tint: "#6366f1" },
  crm_discussion: { icon: MessageSquarePlus, tint: "#6366f1" },
  crm_mention: { icon: AtSign, tint: "#4F6BFF" },
  lead_risk: { icon: AlertTriangle, tint: "#e11b22" },
  overdue_digest: { icon: AlertTriangle, tint: "#e11b22" },
  score_cooling: { icon: TrendingDown, tint: "#dc2626" },
  contract_ar_overdue: { icon: AlertTriangle, tint: "#dc2626" },
  contract_ar_escalated: { icon: AlertTriangle, tint: "#b91c1c" },
  contract_ar_autoinvoice: { icon: FileText, tint: "#2563eb" },
  crm_stage_advanced: { icon: ArrowRight, tint: "#059669" },
  missed_call: { icon: PhoneIncoming, tint: "#e11b22" },
  voicemail_routed: { icon: Voicemail, tint: "#7c3aed" },
  call_briefing: { icon: TrendingUp, tint: "#4f46e5" },
  invoice_gap: { icon: AlertTriangle, tint: "#b91c1c" },
  receipts_over_contract: { icon: AlertTriangle, tint: "#b45309" },
};
const MSG_TYPES = ["mention", "change_order", "phase_overdue", "collection_overdue", "sales_target",
  "quote_stalled", "quote_stalled_escalated", "quote_accepted", "proposal_viewed", "estimate_expiry",
  "settlement_locked", "settlement_pending", "chat_post_failed", "manager_nudge", "perf_month_report", "signing_nudge",
  "price_approval", "price_approved", "price_pending", "site_visit_request", "new_lead", "crm_discussion", "crm_mention", "lead_risk", "overdue_digest", "score_cooling", "contract_ar_overdue", "contract_ar_escalated", "contract_ar_autoinvoice", "crm_stage_advanced",
  "missed_call", "voicemail_routed", "call_briefing", "receipts_over_contract"];

function MiniSparkline({ data, width = 120, height = 28 }) {
  const pts = (data || []).filter((v) => v !== null && v !== undefined);
  if (pts.length < 2) return null;
  const max = Math.max(...pts), min = Math.min(...pts);
  const span = max - min || 1;
  const step = width / (pts.length - 1);
  const coords = pts.map((v, i) => [i * step, height - 4 - ((v - min) / span) * (height - 8)]);
  const path = coords.map((c, i) => `${i === 0 ? "M" : "L"}${c[0].toFixed(1)},${c[1].toFixed(1)}`).join(" ");
  const rising = pts[pts.length - 1] >= pts[0];
  const color = rising ? "#e11b22" : "#16a34a";
  const [lx, ly] = coords[coords.length - 1];
  return (
    <svg width={width} height={height} className="inline-block align-middle" data-testid="digest-sparkline">
      <path d={path} fill="none" stroke={color} strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={lx} cy={ly} r="2.4" fill={color} />
    </svg>
  );
}

export default function Reminders() {
  const { t, lang, reminders, markReminderRead, markAllRemindersRead, user, projects, refresh } = useApp();
  const nav = useNavigate();
  const [grouped, setGrouped] = useState(() => { try { return localStorage.getItem("ali_rem_grouped") !== "0"; } catch { return true; } });
  // 行点击统一改为「弹详情窗」（见 openDetail）；原来「点开即跳转」的 openMention/openLink 已由弹窗内「去处理」承接
  const [expandedDigest, setExpandedDigest] = useState(null);
  const [overdueLeads, setOverdueLeads] = useState([]);
  const [loadingOverdue, setLoadingOverdue] = useState(false);
  const [nudging, setNudging] = useState({});
  const [selectedLeads, setSelectedLeads] = useState({});
  const [batchNudging, setBatchNudging] = useState(false);
  const canNudge = ["admin", "ceo", "sales_vp", "sales_manager"].includes(user.role);
  const toggleDigest = async (r) => {
    if (expandedDigest === r.id) { setExpandedDigest(null); return; }
    setExpandedDigest(r.id); setLoadingOverdue(true); setSelectedLeads({});
    try {
      const { data } = await api.get("/crm/overdue-leads");
      setOverdueLeads(data.leads || []);
    } catch { setOverdueLeads([]); toast.error(lang === "zh" ? "加载超期客户失败" : "Failed to load"); }
    finally { setLoadingOverdue(false); }
  };
  const nudgeSales = async (lead) => {
    setNudging((s) => ({ ...s, [lead.leadId]: true }));
    try {
      const { data } = await api.post(`/crm/leads/${lead.leadId}/nudge-sales`);
      toast.success((lang === "zh" ? "已催办负责销售 " : "Nudged ") + (data.sales || ""));
      setOverdueLeads((ls) => ls.map((x) => x.leadId === lead.leadId ? { ...x, lastNudgeAt: new Date().toISOString() } : x));
    } catch (e) {
      toast.error(e?.response?.data?.detail || (lang === "zh" ? "催办失败" : "Nudge failed"));
    } finally { setNudging((s) => ({ ...s, [lead.leadId]: false })); }
  };
  const batchNudge = async () => {
    const ids = Object.keys(selectedLeads).filter((k) => selectedLeads[k]);
    setBatchNudging(true);
    try {
      const { data } = await api.post("/crm/overdue-leads/batch-nudge", ids.length ? { leadIds: ids } : {});
      toast.success(lang === "zh"
        ? `已催办 ${data.nudged} 户，跳过 ${data.skipped} 户（冷静期内）${data.noSales ? `，${data.noSales} 户无负责销售` : ""}`
        : `Nudged ${data.nudged}, skipped ${data.skipped}`);
      const now = new Date().toISOString();
      setOverdueLeads((ls) => ls.map((x) => (!ids.length || selectedLeads[x.leadId]) ? { ...x, lastNudgeAt: now } : x));
      setSelectedLeads({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || (lang === "zh" ? "批量催办失败" : "Batch nudge failed"));
    } finally { setBatchNudging(false); }
  };
  const toggleSelectAll = () => {
    const allSel = overdueLeads.length > 0 && overdueLeads.every((l) => selectedLeads[l.leadId]);
    if (allSel) setSelectedLeads({});
    else { const m = {}; overdueLeads.forEach((l) => { m[l.leadId] = true; }); setSelectedLeads(m); }
  };
  // 分类 / 区域筛选（反馈 FB973a773a3a）：
  //   ① Comment 与系统自动提醒分开 —— 混在一起时人写的 Comment 会被系统提醒挤出视野；
  //   ② 催办单独一档 —— 概念方案催办一次几十条，混在系统提醒里同样会把别的提醒冲掉
  //      （提交人 Kael 2026-10-17 补充留言）；
  //   ③ 按分公司分开看 —— 与设计部/全系统口径一致：洛杉矶 / 北加湾区 / 圣地亚哥。
  //      ⚠️ 不要合并成「南加 / 北加」：全系统（server.BRANCHES、design_v3.BRANCHES、
  //      users.region、projects.branch）都是三分公司，没有「南加」这一层级。
  // 分类与区域都由后端 /reminders 直接给出（category / region），前端只做筛选，不再二次推断。
  const [cat, setCat] = useState(() => {
    // 铃铛下拉「查看全部提醒」会带 ?cat=comment|nudge|system 过来，落地即按该分类过滤
    try { const c = new URLSearchParams(window.location.search).get("cat"); return ["comment", "nudge", "system"].includes(c) ? c : "all"; } catch { return "all"; }
  });     // all | comment | nudge | system
  const [reg, setReg] = useState("all");     // all | la | norcal | sd
  // 2026-10-20：后端 /reminders 已按「当前账号的受众 + 已读」返回（广播型历史文档按 readByUsers
  // 逐人判定），前端不再做团队二次过滤 —— 旧逻辑会把 toUser 写成脏用户名、或 team 为空的
  // 广播提醒整条丢掉，造成「有同事一条提醒都看不到」。
  const scoped = reminders;
  const catOf = (r) => r.category || "system";
  const list = scoped.filter((r) => (cat === "all" || catOf(r) === cat) && (reg === "all" || r.region === reg));
  const catCounts = {
    all: scoped.length,
    comment: scoped.filter((r) => catOf(r) === "comment").length,
    nudge: scoped.filter((r) => catOf(r) === "nudge").length,
    system: scoped.filter((r) => catOf(r) === "system").length,
  };
  const regCounts = {
    la: scoped.filter((r) => r.region === "la").length,
    norcal: scoped.filter((r) => r.region === "norcal").length,
    sd: scoped.filter((r) => r.region === "sd").length,
  };
  const isEng = user.role === "member" || user.role === "eng_manager";
  const copyScript = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success(lang === "zh" ? "促单话术已复制" : "Script copied"); }
    catch { toast.error(lang === "zh" ? "复制失败，请手动选择" : "Copy failed"); }
  };
  const msgFor = (r) => {
    if (isEng && r.type === "invoice") return lang === "zh" ? "已通知财务部收款" : "Finance has been notified";
    if (r.message && (MSG_TYPES.includes(r.type) || !META[r.type])) return T(r.message);
    return t((META[r.type] || META.invoice).key);
  };

  // 弹窗详情：点开一条提醒 → 弹出详情（打开即标已读，符合「弹窗显示了才算已读」）
  const [detail, setDetail] = useState(null);
  const openDetail = (r) => {
    setDetail(r);
    // 标记已读失败（如后端不可达）不应中断弹窗，也不该抛出未捕获异常
    if (!r.read) Promise.resolve(markReminderRead(r.id)).catch(() => {});
  };
  const goHandle = (r) => {
    setDetail(null);
    if (r.type === "mention") {
      const p = (projects || []).find((x) => x.id === r.projectId);
      if (p) nav(`/project/${p.id}`);
      return;
    }
    if (reminderLink(r)) nav(reminderLink(r));
  };

  const unreadCount = list.filter((r) => !r.read).length;
  const readAll = async (pid) => {
    try { const n = await markAllRemindersRead(pid); toast.success(lang === "zh" ? `已将 ${n} 条提醒标为已读` : `${n} reminder(s) marked read`); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };
  const groups = React.useMemo(() => {
    const m = new Map();
    list.forEach((r) => {
      const key = r.projectId && String(r.projectId).startsWith("P") ? r.projectId : "__other";
      if (!m.has(key)) m.set(key, { key, address: r.address || (projects || []).find((x) => x.id === r.projectId)?.address || "", items: [] });
      const g = m.get(key);
      g.items.push(r);
      if (!g.address && r.address) g.address = r.address;
    });
    return [...m.values()].sort((a, b) => (a.key === "__other") - (b.key === "__other") || b.items.filter((x) => !x.read).length - a.items.filter((x) => !x.read).length);
  }, [list, projects]);
  // 防御：第二参数必须严格为 true 才算「弹窗内渲染」。若有人直接把本函数交给 Array.map，
  // map 会传索引（数字），这里不会被误判（历史 bug：除第一条外全部不可点）。
  const renderItem = (r, opts) => {
            const inDialog = opts === true;
            const m = META[r.type] || META.invoice;
            const Icon = m.icon; const st = payStageByKey(r.stage);
            const showStage = st && !MSG_TYPES.includes(r.type);
            return (
              <div key={inDialog ? `${r.id}-dialog` : r.id} data-testid={`reminder-${r.type}-${r.id}`}
                onClick={inDialog ? undefined : () => openDetail(r)}
                className={`bg-white rounded-xl border p-4 grid grid-cols-[auto_minmax(0,1fr)] items-start gap-3 sm:flex sm:items-center sm:gap-4 transition-colors ${r.read ? "border-slate-100 opacity-70" : "border-slate-200"} ${inDialog ? "" : "cursor-pointer hover:border-brand-300 hover:bg-brand-50/30"}`}>
                <div className="h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: m.tint + "22" }}>
                  <Icon className="h-5 w-5" style={{ color: m.tint }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 break-words">
                    {msgFor(r)}
                    {showStage && <> · <span style={{ color: m.tint }}>{lang === "zh" ? st?.zh : st?.en}</span></>}
                  </p>
                  {(r.address || (!isEng && r.amount)) && (
                    <p className="text-xs text-slate-400 break-words sm:truncate">{r.address}{!isEng && r.amount ? ` · ${fmtMoney(r.amount)}` : ""}</p>
                  )}
                  {r.type === "quote_stalled" && r.script && (
                    <div className="mt-2 rounded-lg bg-amber-50 border border-amber-200 p-2.5" data-testid={`reminder-script-${r.id}`}>
                      <p className="text-[12px] text-slate-700 leading-relaxed whitespace-pre-wrap">{r.script}</p>
                      <div className="flex flex-wrap items-center gap-3 mt-2">
                        <button data-testid={`reminder-copy-script-${r.id}`} onClick={(e) => { e.stopPropagation(); if (!r.read) markReminderRead(r.id); copyScript(r.script); }}
                          className="inline-flex items-center gap-1 text-[12px] font-medium text-amber-700 hover:text-amber-900"><Copy className="h-3.5 w-3.5" />{lang === "zh" ? "复制促单话术" : "Copy script"}</button>
                        {r.proposalToken && (
                          <a data-testid={`reminder-proposal-link-${r.id}`} href={`${window.location.origin}/proposal/${r.proposalToken}`} target="_blank" rel="noreferrer" onClick={(e) => { e.stopPropagation(); if (!r.read) markReminderRead(r.id); }}
                            className="inline-flex items-center gap-1 text-[12px] font-medium text-indigo-600 hover:text-indigo-800"><ExternalLink className="h-3.5 w-3.5" />{lang === "zh" ? "打开业主方案" : "Open proposal"}</a>
                        )}
                      </div>
                    </div>
                  )}
                  {["hrms_dismiss", "hrms_task_remind", "hrms_handover_ack", "hrms_handover_nudge"].includes(r.type) && r.transitionId && <HandoverInline reminder={r} />}
                  {["hrms_duty", "hrms_duty_nudge"].includes(r.type) && r.dutyId && <DutyInline reminder={r} />}
                  {r.type === "progress_nudge" && r.nudgeId && <div className="mt-1.5 max-w-md"><NudgeReplyInline reminder={r} /></div>}
                  {r.type === "pool_rule_assigned" && (r.leadIds || []).length > 0 && <div className="mt-1.5 max-w-xl"><PoolAssignedInline reminder={r} /></div>}
                  {r.type === "recycle_expiring" && r.recycleId && <div className="mt-1.5 max-w-md"><RecycleRestoreInline reminder={r} onChanged={() => { if (!r.read) markReminderRead(r.id); }} /></div>}
                  {r.type === "aftersales_stale" && Array.isArray(r.top) && r.top.length > 0 && <div className="mt-1.5 max-w-xl"><StaleContactInline reminder={r} onAllDone={() => { if (!r.read) markReminderRead(r.id); }} /></div>}
                  {r.type === "proposal_read" && r.leadId && (
                    <button data-testid={`reminder-followup-${r.id}`}
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          const { data } = await api.post(`/crm/leads/${r.leadId}/follow-up-task`, { reminderId: r.id });
                          toast.success(data.existed ? (lang === "zh" ? "今日已有该回访任务" : "Task already exists") : (lang === "zh" ? "已加入今日必做：回访客户" : "Follow-up task added"));
                          if (!r.read) markReminderRead(r.id);
                        } catch (err) { toast.error(err?.response?.data?.detail || (lang === "zh" ? "创建任务失败" : "Failed")); }
                      }}
                      className="mt-2 mr-2 inline-flex items-center gap-1 text-[11px] text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg px-2.5 py-1">
                      {lang === "zh" ? "一键加入今日必做（回访）" : "Add follow-up task"}
                    </button>
                  )}
                  {r.type === "engine_phase_calib_request" && r.calibId && ["admin", "ceo"].includes(user.role) && (
                    <CalibDecideInline r={r} api={api} zh={lang === "zh"} onDone={() => { if (!r.read) markReminderRead(r.id); }} />
                  )}
                  {r.type === "crm_stage_regression" && r.leadId && r.fromStage && (
                    <button data-testid={`reminder-restore-${r.id}`}
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          const { data } = await api.post(`/crm/leads/${r.leadId}/restore-stage`, { to: r.fromStage });
                          toast.success(data.already ? (lang === "zh" ? "客户已在该阶段" : "Already at that stage") : (lang === "zh" ? `已还原到「${r.fromLabel || r.fromStage}」` : `Restored to ${r.fromStage}`));
                          if (!r.read) markReminderRead(r.id);
                        } catch (err) { toast.error(err?.response?.data?.detail || (lang === "zh" ? "还原失败" : "Restore failed")); }
                      }}
                      className="mt-2 mr-2 inline-flex items-center gap-1 text-[11px] text-white bg-cyan-600 hover:bg-cyan-700 rounded-lg px-2.5 py-1">
                      {lang === "zh" ? `一键还原到「${r.fromLabel || r.fromStage}」` : `Restore to ${r.fromStage}`}
                    </button>
                  )}
                  {r.type === "finance_1099" && (
                    <button data-testid={`reminder-w9-sms-${r.id}`}
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (!window.confirm(lang === "zh" ? `向所有 ${r.year || ""} 年缺税号/W-9 的分包商发送短信索要 W-9？` : "SMS all subs missing W-9/Tax ID?")) return;
                        try {
                          const { data } = await api.post("/finance/1099-w9-request-sms", { year: r.year });
                          const bits = [lang === "zh" ? `已发送 ${data.sent} 家` : `Sent ${data.sent}`];
                          if (data.noPhone?.length) bits.push((lang === "zh" ? "无电话：" : "No phone: ") + data.noPhone.join("、"));
                          if (data.failed?.length) bits.push((lang === "zh" ? "发送失败：" : "Failed: ") + data.failed.join("、"));
                          toast.success(bits.join(" · "), { duration: 8000 });
                          if (!r.read) markReminderRead(r.id);
                        } catch (err) { toast.error(err?.response?.data?.detail || (lang === "zh" ? "发送失败" : "Failed")); }
                      }}
                      className="mt-2 mr-2 inline-flex items-center gap-1 text-[11px] text-white bg-rose-600 hover:bg-rose-700 rounded-lg px-2.5 py-1">
                      <Send className="h-3 w-3" />{lang === "zh" ? "一键发短信索要 W-9" : "SMS request W-9"}
                    </button>
                  )}
                  {r.type === "site_visit_request" && r.svid && r.leadId && <SVReschedule r={r} lang={lang} />}
                  {r.type === "co_staff_handover" && r.meta && <HandoverReassignInline r={r} api={api} zh={lang === "zh"} onDone={() => { refresh && refresh(); }} />}
                  {r.type === "change_order" && r.action === "create_invoice" && r.coId && <CoInvoiceOneClick r={r} api={api} zh={lang === "zh"} user={user} onDone={() => { if (!r.read) markReminderRead(r.id); refresh && refresh(); }} />}
                  {r.type === "pr_overpay_override" && r.meta && <OverrideApproveCard r={r} zh={lang === "zh"} user={user} onDone={() => { if (!r.read) markReminderRead(r.id); refresh && refresh(); }} />}
                  {r.type === "pr_unfinished_digest" && r.meta && <UnfinishedDigestPanel r={r} zh={lang === "zh"} />}
                  {r.type === "pr_photo_reminder" && r.meta && <PhotoReminderPanel r={r} zh={lang === "zh"} />}
                  {r.type === "pr_photo_escalation" && r.meta && <PhotoReminderPanel r={r} zh={lang === "zh"} />}
                  {r.type === "pr_photo_urge" && r.meta && <PhotoReminderPanel r={r} zh={lang === "zh"} />}
                  {r.type === "pr_photo_urge_timeout" && r.meta && <PhotoReminderPanel r={r} zh={lang === "zh"} />}
                  {r.type === "pr_pm_photo_ranking" && r.meta && <PmRankingPanel r={r} zh={lang === "zh"} />}
                  {r.type === "pr_photo_escalation_resolved" && r.meta && <EscalationResolvedPanel r={r} zh={lang === "zh"} />}
                  {r.type === "pr_override_review" && r.meta && <OverrideReviewPanel r={r} zh={lang === "zh"} />}
                  {r.type === "design_v3_sign_digest" && r.meta?.items?.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1" data-testid={`reminder-sign-items-${r.id}`} data-count={r.meta.items.length}>
                      {r.meta.items.map((it) => (
                        <button key={it.id} type="button" data-testid={`reminder-sign-item-${it.id}`} title={lang === "zh" ? "打开该项目的签约日期设置框" : "Open this project's sign-date box"}
                          onClick={(e) => { e.stopPropagation(); if (!r.read) markReminderRead(r.id); nav(`/design/sign-dates?focus=${it.id}`); }}
                          className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100">
                          <CalendarCheck className="h-3 w-3" />{(it.address || "").split(",")[0]}{it.date ? <span className="text-amber-600/70">· {it.date}</span> : null}
                        </button>
                      ))}
                      {r.meta.count > r.meta.items.length && <span className="text-[11px] text-slate-400 self-center">{lang === "zh" ? `… 还有 ${r.meta.count - r.meta.items.length} 个` : `… +${r.meta.count - r.meta.items.length} more`}</span>}
                    </div>
                  )}
                  {r.meta?.copyText && (
                    <button data-testid={`reminder-copy-paylink-${r.id}`}
                      onClick={async (e) => { e.stopPropagation(); if (!r.read) markReminderRead(r.id); try { await navigator.clipboard.writeText(r.meta.copyText); toast.success(lang === "zh" ? "付款链接已复制，可直接转发业主" : "Payment link copied"); } catch { toast.error(r.meta.copyText); } }}
                      className="mt-1.5 mr-3 inline-flex items-center gap-1 text-[12px] font-semibold px-2.5 py-1 rounded-md bg-emerald-600 text-white hover:bg-emerald-700"><Copy className="h-3.5 w-3.5" />{lang === "zh" ? "复制付款链接" : "Copy payment link"}</button>
                  )}
                  {(r.type === "payment_request_step" || r.type === "payment_check_pending") && r.meta?.queueLink && (
                    <button data-testid={`reminder-payqueue-${r.id}`} onClick={(e) => { e.stopPropagation(); if (!r.read) markReminderRead(r.id); nav(`${r.meta.queueLink}${r.meta.requestId ? `?req=${r.meta.requestId}` : ""}`); }}
                      className="mt-1.5 mr-3 inline-flex items-center gap-1 text-[12px] font-medium text-violet-700 hover:text-violet-900"><Banknote className="h-3.5 w-3.5" />{lang === "zh" ? "去付款审批队列处理" : "Open payment queue"}</button>
                  )}
                  {reminderLink(r) && (
                    <button data-testid={`reminder-link-${r.id}`} onClick={(e) => { e.stopPropagation(); if (!r.read) markReminderRead(r.id); nav(reminderLink(r)); }}
                      className="mt-1.5 inline-flex items-center gap-1 text-[12px] font-medium text-emerald-600 hover:text-emerald-800"><ExternalLink className="h-3.5 w-3.5" />{
                        r.type === "price_approval" ? (lang === "zh" ? "去审批" : "Review & approve")
                        : r.type === "site_visit_request" ? (lang === "zh" ? "查看勘测预约" : "View site visits")
                        : (r.type === "crm_discussion" || r.type === "crm_mention" || r.type === "new_lead") ? (lang === "zh" ? "查看内部讨论" : "Open discussion")
                        : r.type === "lead_risk" ? (lang === "zh" ? "立即复核" : "Review now")
                        : r.type === "overdue_digest" ? (lang === "zh" ? "查看超期客户" : "View overdue leads")
                        : r.type === "archive_health" ? (lang === "zh" ? "去补档" : "Backfill archives")
                        : r.type === "construction_overdue" ? (lang === "zh" ? "查看超期项目" : "View overdue projects")
                        : r.type === "low_followup" ? (lang === "zh" ? "去跟进客户" : "Follow up lead")
                        : r.type === "call_briefing" ? (lang === "zh" ? "查看通话记录" : "View call log")
                        : r.type === "invoice_gap" ? (lang === "zh" ? "查看开票对账" : "Open reconciliation")
                        : r.type === "receipts_over_contract" ? (lang === "zh" ? "去同步合同额" : "Sync contract")
                        : r.type === "submittal_no_reply" ? (lang === "zh" ? "去跟进交图邮件" : "Follow up submittal")
                        : r.type === "submittal_reply" ? (lang === "zh" ? "查看机构回复" : "View agency reply")
                        : r.type === "submittal_result" ? (lang === "zh" ? "查看审图结果" : "View plan check result")
                        : r.type === "design_owner_signed_sales" || r.type === "crm_contract_signed_design" ? (lang === "zh" ? "查看客户签字文件" : "View signed documents")
                        : r.type === "design_change_confirmed" ? (lang === "zh" ? "查看变更" : "View change")
                        : (r.type === "design_payment_due" || r.type === "design_payment_proof" || r.type === "design_payment_paid" || r.type === "design_payment_escalated") ? (lang === "zh" ? "查看付款页签" : "Open payments")
                        : r.type === "receipt_verify_suspicious" ? (lang === "zh" ? "打开回执核验监控" : "Open verification monitor")
                        : r.type === "design_v3_task_ack" ? (lang === "zh" ? (r.meta?.mine ? "去接受 / 拒绝 / 改期" : "查看待办进展") : (r.meta?.mine ? "Respond to task" : "View task"))
                        : r.type === "design_v3_task_review" ? (lang === "zh" ? "处理拒绝（批准 / 要求继续）" : "Review rejection")
                        : r.type === "design_v3_task_overdue" ? (lang === "zh" ? "查看逾期待办" : "Open overdue task")
                        : r.type === "design_v3_sign_alert" ? (lang === "zh" ? "打开设计项目" : "Open design project")
                        : r.type === "design_v3_sign_digest" ? (lang === "zh" ? "打开签约日期集中设置" : "Open sign-date list")
                        : r.type === "design_v3_approval" ? (lang === "zh" ? "去审批" : "Review approval")
                        : r.type === "design_v3_decision" ? (lang === "zh" ? "查看审批结果" : "View approval result")
                        : r.type === "design_v3_rework" ? (lang === "zh" ? "处理返工" : "Handle rework")
                        : r.type === "design_v3_review" ? (lang === "zh" ? "去复核交图" : "Review submission")
                        : r.type === "design_v3_review_done" ? (lang === "zh" ? "查看复核结果" : "View review result")
                        : r.type === "design_v3_handoff" ? (lang === "zh" ? "查看工程移交" : "View construction handoff")
                        : r.type === "design_v3_receipt_followup" ? (lang === "zh" ? "补齐移交回执" : "Complete handoff receipt")
                        : r.type === "design_v3_stale" ? (lang === "zh" ? "更新项目进展" : "Update project")
                        : r.type === "design_v3_deadline" ? (lang === "zh" ? "处理项目节点" : "Handle milestone")
                        : (r.type === "design_payment_due" || r.type === "design_payment_proof" || r.type === "design_payment_paid" || r.type === "design_payment_escalated") ? (lang === "zh" ? "打开客户付款计划" : "Open payment schedule")
                        : (r.type === "design_v3_task" || r.type === "design_v3_mention" || r.type === "design_v3_reply") ? (lang === "zh" ? "打开设计项目" : "Open design project")
                        : r.type === "signed_unread_digest" ? (lang === "zh" ? (r.count === 1 ? "查看该客户讨论" : "打开已签约客户") : "Open signed customers")
                        : r.type === "signed_receipt_emailed" ? (lang === "zh" ? "查看客户收款" : "Open customer payments")
                        : (r.type === "sub_task_comment" || r.type === "sub_task_evidence" || r.type === "stage_task_locked") ? (lang === "zh" ? "打开任务（任务链）" : "Open task")
                        : r.type === "stage_complete" ? (lang === "zh" ? "一键安排检查 / 验收通过" : "Schedule inspection / pass")
                        : r.type === "sub_co_owner_pays" ? (lang === "zh" ? (r.link && r.link.includes("/crm/signed/") ? "打开已签约客户 · 变更单" : "打开变更订单") : "Open change order")
                        : r.type === "todo_overdue_mgr" ? (lang === "zh" ? "查看逾期待办" : "Open overdue todos")
                        : r.type === "crm_followup" ? (r.link === "/crm/leads?tab=board" ? (lang === "zh" ? "查看客户列表" : "Open customer list") : (lang === "zh" ? "查看客户跟进" : "Open customer follow-up"))
                        : (r.type === "payment_request_step" || r.type === "payment_check_pending") ? (lang === "zh" ? "打开分包卡 · 付款区" : "Open subcontract · payments")
                        : (r.link && r.link.includes("finance-center")) ? (lang === "zh" ? "去审批" : "Review")
                        : (lang === "zh" ? "查看结算详情" : "View settlement")}</button>
                  )}
                  {r.type === "signed_unread_digest" && Array.isArray(r.items) && r.items.length > 1 && (
                    <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid={`reminder-digest-items-${r.id}`}>
                      {r.items.slice(0, 8).map((it) => (
                        <button key={it.leadId} data-testid={`reminder-digest-item-${r.id}-${it.leadId}`} onClick={(e) => { e.stopPropagation(); if (!r.read) markReminderRead(r.id); nav(`/crm/signed/${it.leadId}`); }}
                          className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md border border-violet-200 bg-violet-50 text-violet-800 hover:bg-violet-100">
                          {it.clientName}{it.design ? <span className="text-[10px] text-violet-500">{lang === "zh" ? `设计 ${it.design}` : `D ${it.design}`}</span> : null}{it.eng ? <span className="text-[10px] text-sky-600">{lang === "zh" ? `工程 ${it.eng}` : `E ${it.eng}`}</span> : null}
                        </button>
                      ))}
                    </div>
                  )}
                  {r.phone && (r.type === "missed_call" || r.type === "voicemail_routed") && (
                    <div className="mt-1.5 flex items-center gap-3" data-testid={`reminder-callback-wrap-${r.id}`}>
                      <button data-testid={`reminder-callback-${r.id}`}
                        onClick={(e) => { e.stopPropagation(); window.dispatchEvent(new CustomEvent("softphone:dial", { detail: { number: r.phone } })); if (!r.read) markReminderRead(r.id); }}
                        className="inline-flex items-center gap-1 text-[12px] font-medium px-2.5 py-1 rounded-md bg-emerald-600 text-white hover:bg-emerald-700">
                        <PhoneCall className="h-3.5 w-3.5" />{lang === "zh" ? "一键回拨" : "Call back"}
                      </button>
                      <button data-testid={`reminder-calls-${r.id}`} onClick={(e) => { e.stopPropagation(); if (!r.read) markReminderRead(r.id); nav("/calls"); }}
                        className="inline-flex items-center gap-1 text-[12px] text-slate-500 hover:text-slate-800">
                        <ExternalLink className="h-3.5 w-3.5" />{lang === "zh" ? "通话记录" : "Call log"}
                      </button>
                    </div>
                  )}
                  {r.type === "overdue_digest" && (
                    <div className="mt-2" data-testid={`digest-panel-${r.id}`}>
                      {typeof r.trendDelta === "number" && (
                        <span data-testid={`digest-trend-${r.id}`}
                          className={`inline-flex items-center gap-1 text-[12px] font-semibold px-2 py-0.5 rounded-full ${r.trendDelta > 0 ? "bg-red-50 text-red-600" : r.trendDelta < 0 ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"}`}>
                          {r.trendDelta > 0 ? <TrendingUp className="h-3.5 w-3.5" /> : r.trendDelta < 0 ? <TrendingDown className="h-3.5 w-3.5" /> : <Minus className="h-3.5 w-3.5" />}
                          {r.trendDelta > 0 ? (lang === "zh" ? `较上次新增 ${r.trendDelta} 户（风险扩大）` : `+${r.trendDelta} vs last`)
                            : r.trendDelta < 0 ? (lang === "zh" ? `较上次减少 ${Math.abs(r.trendDelta)} 户（风险收敛）` : `-${Math.abs(r.trendDelta)} vs last`)
                            : (lang === "zh" ? "较上次持平" : "No change")}
                        </span>
                      )}
                      {Array.isArray(r.trend7) && r.trend7.filter((v) => v != null).length >= 2 && (
                        <span className="ml-2 inline-flex items-center gap-1.5 align-middle" data-testid={`digest-trend7-${r.id}`} title={lang === "zh" ? "近 7 天超期户数走势" : "7-day trend"}>
                          <span className="text-[11px] text-slate-400">{lang === "zh" ? "7日走势" : "7d"}</span>
                          <MiniSparkline data={r.trend7} />
                        </span>
                      )}
                      <button data-testid={`digest-expand-${r.id}`} onClick={(e) => { e.stopPropagation(); toggleDigest(r); }}
                        className="ml-2 inline-flex items-center gap-1 text-[12px] font-medium text-red-600 hover:text-red-800">
                        {expandedDigest === r.id ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                        {expandedDigest === r.id ? (lang === "zh" ? "收起清单" : "Collapse") : (lang === "zh" ? "展开超期清单并催办" : "Expand & nudge")}
                      </button>
                      {expandedDigest === r.id && (
                        <div className="mt-2 rounded-lg border border-slate-200" data-testid={`digest-list-${r.id}`}>
                          {loadingOverdue ? (
                            <p className="p-3 text-[12px] text-slate-400">{lang === "zh" ? "加载中…" : "Loading…"}</p>
                          ) : overdueLeads.length === 0 ? (
                            <p className="p-3 text-[12px] text-slate-400">{lang === "zh" ? "暂无超期客户 ✓" : "No overdue leads ✓"}</p>
                          ) : (
                            <>
                              {canNudge && (
                                <div className="flex flex-wrap items-center justify-between gap-2 px-2.5 py-2 border-b border-slate-100 bg-slate-50 rounded-t-lg">
                                  <label className="flex items-center gap-1.5 text-[12px] text-slate-600 cursor-pointer">
                                    <input type="checkbox" data-testid={`digest-select-all-${r.id}`}
                                      checked={overdueLeads.length > 0 && overdueLeads.every((l) => selectedLeads[l.leadId])}
                                      onChange={toggleSelectAll} className="accent-brand-500" />
                                    {lang === "zh" ? "全选" : "Select all"}
                                  </label>
                                  <button data-testid={`digest-batch-nudge-${r.id}`} disabled={batchNudging} onClick={(e) => { e.stopPropagation(); if (!r.read) markReminderRead(r.id); batchNudge(); }}
                                    className="inline-flex items-center gap-1 text-[12px] font-medium px-3 py-1 rounded-md bg-brand-500 text-white hover:bg-brand-600 disabled:opacity-50">
                                    <Send className="h-3.5 w-3.5" />{(() => {
                                      const n = Object.values(selectedLeads).filter(Boolean).length;
                                      return lang === "zh" ? (n ? `催办所选 (${n})` : `一键催办全部 (${overdueLeads.length})`) : (n ? `Nudge selected (${n})` : `Nudge all (${overdueLeads.length})`);
                                    })()}
                                  </button>
                                </div>
                              )}
                              <div className="divide-y divide-slate-100">
                                {overdueLeads.map((ld) => (
                                  <div key={ld.leadId} data-testid={`digest-row-${ld.leadId}`} className="flex flex-wrap items-center gap-3 p-2.5">
                                    {canNudge && (
                                      <input type="checkbox" data-testid={`digest-row-select-${ld.leadId}`}
                                        checked={!!selectedLeads[ld.leadId]}
                                        onChange={() => setSelectedLeads((s) => ({ ...s, [ld.leadId]: !s[ld.leadId] }))}
                                        onClick={(e) => e.stopPropagation()} className="accent-brand-500 flex-shrink-0" />
                                    )}
                                    <div className="flex-1 min-w-0">
                                      <p className="text-[13px] font-medium text-slate-800 truncate">
                                        {ld.clientName}
                                        {ld.isNew && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-600 font-semibold">{lang === "zh" ? "今日新增" : "NEW"}</span>}
                                      </p>
                                      <p className="text-[11px] text-slate-400 truncate">{ld.stageZh} · {lang === "zh" ? `停留 ${ld.days} 天（超 ${ld.overBy} 天）` : `${ld.days}d`} · {ld.sales}</p>
                                    </div>
                                    <button data-testid={`digest-goto-${ld.leadId}`} onClick={(e) => { e.stopPropagation(); if (!r.read) markReminderRead(r.id); nav(`/crm/leads/${ld.leadId}`); }}
                                      className="text-[11px] text-slate-500 hover:text-slate-800 flex-shrink-0">{lang === "zh" ? "详情" : "Detail"}</button>
                                    {canNudge && (
                                      <button data-testid={`digest-nudge-${ld.leadId}`} disabled={nudging[ld.leadId]} onClick={(e) => { e.stopPropagation(); if (!r.read) markReminderRead(r.id); nudgeSales(ld); }}
                                        className="flex-shrink-0 inline-flex items-center gap-1 text-[11px] font-medium px-2.5 py-1 rounded-md bg-brand-500 text-white hover:bg-brand-600 disabled:opacity-50">
                                        <Send className="h-3 w-3" />{ld.lastNudgeAt ? (lang === "zh" ? "已催办" : "Nudged") : (lang === "zh" ? "催办销售" : "Nudge")}
                                      </button>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="col-span-full flex flex-wrap items-center gap-2 sm:block sm:text-right flex-shrink-0">
                  {/* 已读 / 未读 明确标识 */}
                  <span data-testid={`reminder-state-${r.id}`}
                    className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${r.read ? "bg-slate-100 text-slate-500" : "bg-brand-50 text-brand-600"}`}>
                    <span className={`h-1.5 w-1.5 rounded-full ${r.read ? "bg-slate-400" : "bg-brand-500"}`} />
                    {r.read ? (lang === "zh" ? "已读" : "Read") : (lang === "zh" ? "未读" : "Unread")}
                  </span>
                  <p className="text-[11px] text-slate-400 mt-0.5">{fmtD(r.createdAt)}</p>
                  {!r.read && <button onClick={() => markReminderRead(r.id)} className="text-xs text-slate-500 hover:text-slate-800 mt-1">{lang === "zh" ? "标记已读" : "Mark read"}</button>}
                </div>
              </div>
            );
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2 flex-wrap">
        <Bell className="h-6 w-6 text-red-600" />
        <h1 className="text-2xl font-bold text-slate-900">{t("remindersTitle")}</h1>
        {unreadCount > 0 && <span className="text-xs font-semibold text-rose-600 bg-rose-50 px-2 py-0.5 rounded-full" data-testid="reminders-unread-count">{unreadCount}</span>}
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <button type="button" data-testid="reminders-group-toggle" onClick={() => setGrouped((g) => { try { localStorage.setItem("ali_rem_grouped", g ? "0" : "1"); } catch {} return !g; })}
            className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-lg border ${grouped ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}><Layers className="h-3.5 w-3.5" />{lang === "zh" ? "按项目分组" : "Group by project"}</button>
          {unreadCount > 0 && <button type="button" data-testid="reminders-read-all" onClick={() => readAll()} className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-brand-500 text-white hover:bg-brand-600"><CheckCheck className="h-3.5 w-3.5" />{lang === "zh" ? `全部已读（${unreadCount}）` : `Mark all read (${unreadCount})`}</button>}
          <TeamReminderOverview />
          <ReminderPrefs />
        </div>
      </div>

      <PersonnelBusinessInbox />

      {/* 分类 / 区域筛选条（反馈 FB973a773a3a）：把「人写的 Comment」「催办」和「系统自动提醒」分开，
          并按分公司（洛杉矶 / 北加湾区 / 圣地亚哥）分别查看。字段由后端 /reminders 直接返回。 */}
      <div className="flex flex-wrap items-center gap-1.5" data-testid="reminders-filters">
        <span className="text-[11px] font-semibold text-slate-400 mr-0.5">{lang === "zh" ? "分类" : "Type"}</span>
        {[["all", lang === "zh" ? "全部" : "All", catCounts.all], ["comment", lang === "zh" ? "评论 / @我" : "Comments", catCounts.comment], ["nudge", lang === "zh" ? "催办" : "Nudges", catCounts.nudge], ["system", lang === "zh" ? "系统提醒" : "System", catCounts.system]].map(([k, label, n]) => (
          <button key={k} type="button" data-testid={`reminders-cat-${k}`} data-active={cat === k ? 1 : 0} onClick={() => setCat(k)}
            className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg border transition-colors ${cat === k ? (REM_CAT_ACTIVE[k] || "bg-slate-900 text-white border-slate-900") : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>
            {k === "comment" && <AtSign className="h-3.5 w-3.5" />}{k === "nudge" && <BellRing className="h-3.5 w-3.5" />}{label}<span className={`text-[10px] ${cat === k ? "opacity-80" : "text-slate-400"}`}>{n}</span>
          </button>
        ))}
        <span className="mx-1 h-4 w-px bg-slate-200" />
        <span className="text-[11px] font-semibold text-slate-400 mr-0.5">{lang === "zh" ? "分公司" : "Branch"}</span>
        {[["all", lang === "zh" ? "全部" : "All"], ["la", lang === "zh" ? "洛杉矶" : "LA"], ["norcal", lang === "zh" ? "北加湾区" : "NorCal"], ["sd", lang === "zh" ? "圣地亚哥" : "SD"]].map(([k, label]) => (
          <button key={k} type="button" data-testid={`reminders-region-${k}`} data-active={reg === k ? 1 : 0} onClick={() => setReg(k)}
            className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg border transition-colors ${reg === k ? "bg-brand-500 text-white border-brand-500" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>
            {label}{k !== "all" && <span className={`text-[10px] ${reg === k ? "opacity-80" : "text-slate-400"}`}>{regCounts[k]}</span>}
          </button>
        ))}
        {(cat !== "all" || reg !== "all") && (
          <button type="button" data-testid="reminders-filters-clear" onClick={() => { setCat("all"); setReg("all"); }} className="ml-1 text-xs font-semibold text-brand-600 hover:underline">
            {lang === "zh" ? "清除筛选" : "Clear"}
          </button>
        )}
      </div>

      {list.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center" data-testid="reminders-empty">
          <BellOff className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-400">{cat !== "all" || reg !== "all" ? (lang === "zh" ? "没有符合筛选条件的提醒" : "No reminders match the filters") : t("noReminders")}</p>
        </div>
      ) : grouped ? (
        <div className="space-y-5" data-testid="reminders-grouped">
          {groups.map((g) => {
            const gu = g.items.filter((x) => !x.read).length;
            return (
              <section key={g.key} data-testid={`reminders-group-${g.key}`} className="space-y-2">
                <div className="flex items-center gap-2 px-1">
                  <Building2 className="h-4 w-4 text-slate-400" />
                  <h2 className="text-sm font-bold text-slate-700 truncate">{g.key === "__other" ? (lang === "zh" ? "其他 / 非项目提醒" : "Other") : (g.address || g.key)}</h2>
                  <span className="text-[11px] text-slate-400">{g.items.length}</span>
                  {gu > 0 && <span className="text-[11px] font-semibold text-rose-600" data-testid={`reminders-group-unread-${g.key}`}>{gu} {lang === "zh" ? "未读" : "unread"}</span>}
                  {g.key !== "__other" && <button type="button" data-testid={`reminders-group-open-${g.key}`} onClick={() => nav(`/project/${g.key}`)} className="text-[11px] text-brand-600 hover:underline inline-flex items-center gap-0.5"><ExternalLink className="h-3 w-3" />{lang === "zh" ? "打开项目" : "Open"}</button>}
                  {gu > 0 && g.key !== "__other" && <button type="button" data-testid={`reminders-group-read-${g.key}`} onClick={() => readAll(g.key)} className="ml-auto text-[11px] font-medium text-slate-500 hover:text-slate-800 inline-flex items-center gap-0.5"><CheckCheck className="h-3 w-3" />{lang === "zh" ? "本项目全部已读" : "Mark project read"}</button>}
                </div>
                {/* 注意：必须用箭头包一层——直接把 renderItem 交给 map，第二个参数会收到「索引」，
                    被当成 inDialog=true，导致除第一条外全部失去点击 */}
                <div className="space-y-2">{g.items.map((r) => renderItem(r))}</div>
              </section>
            );
          })}
        </div>
      ) : (
        <div className="space-y-2">{list.map((r) => renderItem(r))}</div>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }}>
        <DialogContent className="max-w-lg" data-testid="reminder-detail-modal">
          <DialogHeader>
            <DialogTitle className="text-sm font-semibold text-slate-800 flex items-center gap-2">
              {detail && (() => { const M = META[detail.type] || META.invoice; const I = M.icon; return <I className="h-4 w-4" style={{ color: M.tint }} />; })()}
              {lang === "zh" ? "提醒详情" : "Reminder detail"}
            </DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-3">
              {/* 直接复用整张提醒卡片：完整文案 + 全部快捷操作按钮（点按钮同样会标已读） */}
              {renderItem(detail, true)}
              <div className="flex items-center justify-end gap-2">
                {(reminderLink(detail) || detail.type === "mention") && (
                  <button type="button" data-testid="reminder-detail-handle" onClick={() => goHandle(detail)} className="px-3 py-1.5 rounded-lg bg-brand-500 text-white text-sm font-medium hover:bg-brand-600 inline-flex items-center gap-1"><ExternalLink className="h-3.5 w-3.5" />{lang === "zh" ? "去处理" : "Open"}</button>
                )}
                <button type="button" data-testid="reminder-detail-close" onClick={() => setDetail(null)} className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50">{lang === "zh" ? "关闭" : "Close"}</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
}

function SVReschedule({ r, lang }) {
  const { markReminderRead } = useApp();
  const [date, setDate] = useState(r.svDate || "");
  const [slot, setSlot] = useState(r.svSlot || "上午 AM");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const SLOTS = ["上午 AM", "下午 PM", "傍晚 Evening"];
  if (done) return <p className="mt-1.5 text-[11px] text-emerald-600" data-testid={`sv-resched-done-${r.id}`}>{lang === "zh" ? "✓ 已改约并通知业主" : "✓ Rescheduled & owner notified"}</p>;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2" data-testid={`reminder-sv-resched-${r.id}`} onClick={(e) => e.stopPropagation()}>
      <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid={`sv-resched-date-${r.id}`}
        className="text-[12px] rounded-md border border-slate-200 px-2 py-1" />
      <select value={slot} onChange={(e) => setSlot(e.target.value)} data-testid={`sv-resched-slot-${r.id}`}
        className="text-[12px] rounded-md border border-slate-200 px-2 py-1 bg-white">
        {SLOTS.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
      <button data-testid={`sv-resched-submit-${r.id}`} disabled={busy || !date}
        onClick={async () => {
          setBusy(true);
          try {
            const { data } = await api.post(`/crm/leads/${r.leadId}/site-visits/${r.svid}/reschedule`, { date, timeSlot: slot, notifyOwner: true });
            toast.success(lang === "zh"
              ? `已改约至 ${date} ${slot}${(data.notified || []).length ? "，已通知业主（" + data.notified.map((n) => n === "sms" ? "短信" : "邮件").join("+") + "）" : "（业主无联系方式，未发送通知）"}`
              : `Rescheduled to ${date} ${slot}`);
            setDone(true);
            if (!r.read) markReminderRead(r.id);
          } catch (err) { toast.error(err?.response?.data?.detail || (lang === "zh" ? "改约失败" : "Reschedule failed")); }
          setBusy(false);
        }}
        className="inline-flex items-center gap-1 text-[11px] text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg px-2.5 py-1 disabled:opacity-50">
        {lang === "zh" ? "改约并通知业主" : "Reschedule & notify owner"}
      </button>
    </div>
  );
}
